import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Signal,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  SmoothBlogNavigation,
  NavigationWidth,
  SubscribeButton,
  SmoothNavigation,
  LineEffect,
  MenuToogle,
} from '@pyg-animations/navigation';
import { LanguageService } from '@pyg-services/language/language.service';
import { GetDemoComponent } from '@pyg-shared/get-demo/get-demo.component';
import { PopupSubscribeComponent } from './popup-subscribe/popup-subscribe.component';
import { DOCUMENT } from '@angular/common';
import { ClickOutsideDirective } from '@pyg-directives/click_outside.directive';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap, tap } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import { SectionActivePipe } from 'src/app/pipes/section_active.pipe';
import { APP_LANGS } from '@pyg-services/language/language.constants';
import { Langs } from '@pyg-services/language/language.types';

type NavWidthType = '' | 'pygWidth' | 'blogWidth';

@Component({
  selector: 'app-navigate',
  templateUrl: './navigate.component.html',
  styleUrls: ['./navigate.component.scss'],
  animations: [
    SmoothNavigation,
    SmoothBlogNavigation,
    NavigationWidth,
    SubscribeButton,
    LineEffect,
    MenuToogle,
  ],
  imports: [
    RouterLink,
    TranslatePipe,
    GetDemoComponent,
    PopupSubscribeComponent,
    ClickOutsideDirective,
    SectionActivePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigateComponent implements AfterViewInit {
  private readonly router = inject(Router);
  private readonly langService = inject(LanguageService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly DOC = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private scrollHeigth = 65;
  private subscribeExist = false;

  public readonly subscribeOpened = signal(false);
  public readonly languages = APP_LANGS;
  public readonly langPath = this.langService.langPath;
  public readonly activeLanguage = this.langService.activeLanguage;
  public readonly menuOpen = signal(false);
  public readonly menuImg = signal('menu.svg');
  public readonly productsIsActive = signal(false);
  public readonly activeSiteSection = signal<string>('');
  public readonly navChange = signal(false);
  public readonly navigationWidth: Signal<NavWidthType>;
  public readonly navAnimeTrigger = computed(() =>
    this.activeSiteSection() !== 'blog' && this.navChange() ? 'true' : 'false',
  );

  public readonly navBlogAnimeTrigger = computed(() =>
    this.activeSiteSection() === 'blog' && this.navChange() ? 'true' : 'false',
  );

  public readonly pygBgActive = computed(
    () => this.productsIsActive() && !this.navChange(),
  );

  @ViewChild('pygNavigation') pygNavigation?: ElementRef<HTMLElement>;

  constructor() {
    fromEvent(this.DOC, 'scroll')
      .pipe(
        tap((event) => this.scroll(event.target as Document)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    const isMobile = toSignal(
      this.breakpointObserver
        .observe('(max-width: 1024px)')
        .pipe(map((result) => result.matches)),
    );

    this.navigationWidth = computed(() => {
      let width: NavWidthType = '';
      if (!isMobile()) {
        width =
          this.navBlogAnimeTrigger() === 'true' ? 'blogWidth' : 'pygWidth';
      }
      return width;
    });

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.SiteURLActiveCheck(event);
        }
      });
  }

  ngAfterViewInit(): void {
    this.scroll(this.DOC);
  }

  scroll(target: Document): void {
    const scrollY = target.scrollingElement?.scrollTop;
    if (!this.pygNavigation || !scrollY) {
      return;
    }

    const classList = this.pygNavigation.nativeElement.classList;
    const activeSite = this.activeSiteSection();

    if (scrollY >= this.scrollHeigth) {
      this.navChange.set(true);
      classList.add('navbarScroll');
      classList.remove('navbarTop');
      this.scrollHeigth = 20;
      if (activeSite === 'products' || activeSite === 'futureoftelco') {
        this.productsIsActive.set(false);
      }
    } else {
      this.navChange.set(false);
      this.subscribeOpened.set(false);
      classList.add('navbarTop');
      classList.remove('navbarScroll');
      this.scrollHeigth = 65;
      if (
        (activeSite === 'products' || activeSite === 'futureoftelco') &&
        !this.menuOpen()
      ) {
        this.productsIsActive.set(true);
      }
    }
  }

  openMenu() {
    this.menuOpen.set(!this.menuOpen());
    const activeSite = this.activeSiteSection();
    if (this.menuOpen()) {
      this.menuImg.set('x.svg');
      if (activeSite === 'products' || activeSite === 'futureoftelco') {
        this.productsIsActive.set(false);
      }
    } else {
      if (activeSite === 'products' || activeSite === 'futureoftelco') {
        this.productsIsActive.set(true);
      }
      this.menuImg.set('menu.svg');
    }
  }

  hideMenu() {
    this.menuOpen.set(false);
    this.menuImg.set('menu.svg');
    const activeSite = this.activeSiteSection();
    if (activeSite === 'products' || activeSite === 'futureoftelco') {
      this.productsIsActive.set(true);
    } else {
      this.productsIsActive.set(false);
    }
  }

  toElement(element: string) {
    if (element === 'industry') {
      this.activeSiteSection.set('industry');
    } else if (element === 'instore') {
      this.activeSiteSection.set('');
    }
    setTimeout(() => {
      this.DOC.getElementById(element)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }, 400);
  }

  toogleSubscribeForm() {
    this.subscribeOpened.set(!this.subscribeOpened());
    this.focusOut();
    setTimeout(() => {
      if (this.subscribeOpened()) {
        this.subscribeExist = true;
      }
    }, 300);
  }

  focusOut() {
    if (this.subscribeExist) {
      this.subscribeOpened.set(false);
      this.subscribeExist = false;
    }
  }

  setLanguage(language: Langs): void {
    this.langService
      .setLanguage(language)
      .pipe(
        switchMap(() =>
          this.router.navigate([language + this.router.url.slice(3)]),
        ),
      )
      .subscribe();
  }

  private SiteURLActiveCheck(event: NavigationEnd): void {
    if (
      event.url.indexOf('products') !== -1 ||
      event.url.indexOf('in-store-analytics') !== -1
    ) {
      this.activeSiteSection.set('products');
    } else if (
      event.url.indexOf('shopguard') !== -1 ||
      event.url.indexOf('ai-theft-protection') !== -1
    ) {
      this.activeSiteSection.set('shopguard');
    } else if (event.url.indexOf('getdemo') !== -1) {
      this.activeSiteSection.set('getdemo');
    } else if (event.url.indexOf('privacy-policy') !== -1) {
      this.activeSiteSection.set('privacy-policy');
    } else if (event.url.indexOf('futureoftelco') !== -1) {
      this.activeSiteSection.set('futureoftelco');
    } else if (event.url.indexOf('blog') !== -1) {
      this.activeSiteSection.set('blog');
    } else {
      this.activeSiteSection.set('');
    }

    if (
      this.activeSiteSection() === 'products' ||
      this.activeSiteSection() === 'futureoftelco'
    ) {
      this.productsIsActive.set(true);
    } else {
      this.productsIsActive.set(false);
    }
  }
}
